import { Avatar, AvatarFallback } from '@workspace/ui/components/avatar'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@workspace/ui/components/tooltip'
import { cn, visitorLabel } from '@workspace/ui/lib/utils'
import {
  ChevronDown,
  ChevronRight,
  Clock,
  Eye,
  FileText,
  Link2,
  Loader2,
  MousePointerClick,
} from 'lucide-react'
import { useEffect, useState } from 'react'
import { MetricTooltip } from './metric-tooltip.js'
import { DECISION_STAGE_TOOLTIPS, ENGAGEMENT_TOOLTIPS } from './metric-tooltips.js'
import type { InvestorActivity } from './types.js'
import type { VisitorIntelligenceData } from './visitor-intelligence.js'

/** Timeline colors for viewing time sections */
const TIMELINE_COLORS: Record<string, string> = {
  profile: 'bg-blue-500/60',
  document: 'bg-emerald-500/60',
  pitch: 'bg-[#f97316]/60',
}

/** Dot colors for instant events */
const EVENT_DOT_COLORS: Record<string, string> = {
  pitch_play: 'bg-[#f97316]',
  linkedin_click: 'bg-[#0a66c2]',
  dimension_expand: 'bg-amber-400',
  signal_expand: 'bg-amber-300',
  contact_founder: 'bg-pink-500',
}

const TIMELINE_LABELS: Record<string, string> = {
  profile: 'Viewing profile',
  document: 'Viewing document',
  pitch: 'Watching pitch',
  pitch_play: 'Played pitch video',
  linkedin_click: 'LinkedIn click',
  dimension_expand: 'Expanded metric',
  signal_expand: 'Expanded signal',
  contact_founder: 'Connected with founder',
}

function formatSeconds(seconds: number): string {
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

const ENGAGEMENT_BADGE: Record<string, { label: string; color: string }> = {
  summary_only: { label: 'Summary Only', color: 'bg-zinc-500/15 text-zinc-400' },
  quick_scan: { label: 'Quick Scan', color: 'bg-blue-500/15 text-blue-400' },
  engaged: { label: 'Engaged', color: 'bg-emerald-500/15 text-emerald-400' },
  deep_dive: { label: 'Deep Dive', color: 'bg-[#f97316]/15 text-[#f97316]' },
}

const STAGE_BADGE: Record<string, { label: string; color: string }> = {
  screening: { label: 'Screening', color: 'text-zinc-400' },
  active_evaluation: { label: 'Evaluating', color: 'text-blue-400' },
  serious_interest: { label: 'Serious', color: 'text-emerald-400' },
  comparing: { label: 'Comparing', color: 'text-amber-400' },
}

/** Parse "1m 30s", "2m", "0m 55s", "45s" into total seconds */
function parseDuration(duration: string): number {
  let total = 0
  const minMatch = duration.match(/(\d+)m/)
  const secMatch = duration.match(/(\d+)s/)
  if (minMatch?.[1]) total += parseInt(minMatch[1], 10) * 60
  if (secMatch?.[1]) total += parseInt(secMatch[1], 10)
  return total
}

export function InvestorRow({
  investor,
  expanded,
  onToggle,
  linkName,
  onLoadIntelligence,
}: {
  investor: InvestorActivity
  expanded: boolean
  onToggle: () => void
  linkName?: string
  onLoadIntelligence?: () => Promise<VisitorIntelligenceData>
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-4 px-6 py-3.5 text-left transition-colors hover:bg-white/[0.02]"
      >
        <Avatar className="size-8 shrink-0">
          <AvatarFallback className="bg-elevated text-[10px] font-medium text-white">
            {investor.initials}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="text-sm font-medium text-foreground">{visitorLabel(investor.firm)}</p>
            {investor.engagementLevel && ENGAGEMENT_BADGE[investor.engagementLevel] && (
              <span className="inline-flex items-center gap-1">
                <span
                  className={cn(
                    'rounded px-1.5 py-0.5 text-[10px] font-medium',
                    ENGAGEMENT_BADGE[investor.engagementLevel]?.color
                  )}
                >
                  {ENGAGEMENT_BADGE[investor.engagementLevel]?.label}
                </span>
                {ENGAGEMENT_TOOLTIPS[investor.engagementLevel] && (
                  <MetricTooltip text={ENGAGEMENT_TOOLTIPS[investor.engagementLevel]!} />
                )}
              </span>
            )}
            {investor.decisionStage && STAGE_BADGE[investor.decisionStage] && (
              <span className="inline-flex items-center gap-1">
                <span
                  className={cn(
                    'text-[10px] font-medium',
                    STAGE_BADGE[investor.decisionStage]?.color
                  )}
                >
                  {STAGE_BADGE[investor.decisionStage]?.label}
                </span>
                {DECISION_STAGE_TOOLTIPS[investor.decisionStage] && (
                  <MetricTooltip text={DECISION_STAGE_TOOLTIPS[investor.decisionStage]!} />
                )}
              </span>
            )}
            {linkName && (
              <span className="flex items-center gap-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[10px] text-emerald-400">
                <Link2 className="size-2.5" />
                {linkName}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center gap-3 text-[10px] text-muted-foreground">
            <span>{investor.lastVisit}</span>
            {investor.topInterest && (
              <span className="text-foreground/60">Top: {investor.topInterest}</span>
            )}
          </div>
        </div>
        {/* Metric pills */}
        <div className="flex shrink-0 items-center gap-1.5">
          <span className="inline-flex items-center gap-1 rounded-md bg-blue-500/10 px-2 py-1 text-[10px] font-medium text-blue-400">
            <Eye className="size-3" />
            {investor.visits}
          </span>
          <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-1 text-[10px] font-medium text-emerald-400">
            <Clock className="size-3" />
            {investor.timeSpent}
          </span>
          {(() => {
            const clicks = investor.sessions.reduce(
              (sum, s) =>
                sum + s.events.filter((e) => !e.duration || parseDuration(e.duration) === 0).length,
              0
            )
            return clicks > 0 ? (
              <span className="inline-flex items-center gap-1 rounded-md bg-[#f97316]/10 px-2 py-1 text-[10px] font-medium text-[#f97316]">
                <MousePointerClick className="size-3" />
                {clicks}
              </span>
            ) : null
          })()}
        </div>
        <ChevronDown
          className={cn(
            'size-4 shrink-0 text-muted-foreground transition-transform',
            expanded && 'rotate-180'
          )}
        />
      </button>

      {expanded && <InvestorDetail investor={investor} onLoadIntelligence={onLoadIntelligence} />}
    </div>
  )
}

/** Collapsible section within investor detail */
function DetailSection({
  title,
  defaultOpen = false,
  children,
}: {
  title: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="rounded-lg border border-border/30 bg-muted/10">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left"
      >
        <ChevronRight
          className={cn('size-3 text-muted-foreground transition-transform', open && 'rotate-90')}
        />
        <span className="text-[11px] font-medium text-muted-foreground">{title}</span>
      </button>
      {open && <div className="border-t border-border/20 px-3 py-2.5">{children}</div>}
    </div>
  )
}

/** Merged arguments + risks sorted by dwell time, top 5 visible */
function ContentEngagement({
  args,
  risks,
}: {
  args: VisitorIntelligenceData['argumentBreakdown']
  risks: VisitorIntelligenceData['riskBreakdown']
}) {
  const [showAll, setShowAll] = useState(false)

  // Merge into one list sorted by dwell time
  type ContentItem = {
    key: string
    title: string
    dwellMs: number
    expansionCount: number
    kind: 'strength' | 'concern' | 'risk'
    severity?: string
  }

  const items: ContentItem[] = [
    ...args.map((a) => ({
      key: `arg-${a.argumentId}`,
      title: a.title,
      dwellMs: a.dwellMs,
      expansionCount: a.expansionCount,
      kind: a.sentiment === 'strength' ? ('strength' as const) : ('concern' as const),
    })),
    ...risks.map((r) => ({
      key: `risk-${r.riskId}`,
      title: r.title,
      dwellMs: r.dwellMs,
      expansionCount: r.expansionCount,
      kind: 'risk' as const,
      severity: r.severity,
    })),
  ].sort((a, b) => b.dwellMs - a.dwellMs)

  if (items.length === 0) return null

  const visible = showAll ? items : items.slice(0, 5)
  const hiddenCount = items.length - 5

  const dotColor = (item: ContentItem) => {
    if (item.kind === 'strength') return 'bg-emerald-400'
    if (item.kind === 'concern') return 'bg-amber-400'
    if (item.severity === 'critical') return 'bg-red-400'
    if (item.severity === 'high') return 'bg-amber-400'
    return 'bg-blue-400'
  }

  const kindLabel = (item: ContentItem) => {
    if (item.kind === 'risk') return 'risk'
    return item.kind
  }

  return (
    <div className="mb-3">
      <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        Content Engagement ({items.length})
      </p>
      <div className="space-y-1">
        {visible.map((item) => (
          <div key={item.key} className="flex items-center gap-2">
            <span className={cn('size-1.5 shrink-0 rounded-full', dotColor(item))} />
            <span className="flex-1 truncate text-[11px] text-foreground">{item.title}</span>
            <span className="text-[9px] text-muted-foreground/60">{kindLabel(item)}</span>
            <span className="text-[10px] text-muted-foreground tabular-nums">
              {item.expansionCount > 1 ? `${item.expansionCount}× · ` : ''}
              {formatDuration(Math.round(item.dwellMs / 1000))}
            </span>
          </div>
        ))}
      </div>
      {!showAll && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(true)}
          className="mt-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
        >
          Show {hiddenCount} more...
        </button>
      )}
      {showAll && hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setShowAll(false)}
          className="mt-1.5 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
        >
          Show less
        </button>
      )}
    </div>
  )
}

/** Investor detail with hierarchical content sorted by engagement */
export function InvestorDetail({
  investor,
  onLoadIntelligence,
  showSessions = true,
}: {
  investor: InvestorActivity
  onLoadIntelligence?: () => Promise<VisitorIntelligenceData>
  /** Off for a summary-first surface (the Share page's Visitors log) where the
   * per-session timeline drill-down is a second level of detail nobody asked
   * for — on for the admin/investor-feedback views that still want it. */
  showSessions?: boolean
}) {
  const [intelligence, setIntelligence] = useState<VisitorIntelligenceData | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!onLoadIntelligence || intelligence) return
    setLoading(true)
    onLoadIntelligence()
      .then(setIntelligence)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [onLoadIntelligence, intelligence])

  // Build activity items sorted by time spent (highest first)
  const activityItems: { key: string; label: string; seconds: number; type: string }[] = []

  // Section viewing times (excluding profile)
  const sectionTotals = new Map<string, number>()
  for (const s of investor.sessions) {
    for (const ev of s.events) {
      if (ev.duration) {
        const sec = parseDuration(ev.duration)
        if (sec > 0 && ev.type !== 'profile') {
          const label = ev.type === 'document' ? ev.label : ev.type
          sectionTotals.set(label, (sectionTotals.get(label) ?? 0) + sec)
        }
      }
    }
  }
  for (const [label, seconds] of sectionTotals) {
    activityItems.push({ key: `section-${label}`, label, seconds, type: 'section' })
  }

  // Dimension dwell from intelligence data
  if (intelligence?.dimensionBreakdown) {
    const dimLabels: Record<string, string> = {
      team: 'Team',
      traction: 'Traction',
      market: 'Market',
      problem_solution: 'Problem / Solution',
      vision: 'Vision',
    }
    for (const dim of intelligence.dimensionBreakdown) {
      if (dim.dwellSeconds > 0) {
        const label = dimLabels[dim.dimension] ?? dim.dimension
        activityItems.push({
          key: `dim-${dim.dimension}`,
          label,
          seconds: dim.dwellSeconds,
          type: 'dimension',
        })
      }
    }
  }

  // Document viewing
  if (investor.documentViewing) {
    for (const doc of investor.documentViewing) {
      if (doc.totalSeconds > 0) {
        // Don't double-count if already in sectionTotals
        if (!sectionTotals.has(doc.documentName)) {
          activityItems.push({
            key: `doc-${doc.documentId}`,
            label: doc.documentName,
            seconds: doc.totalSeconds,
            type: 'document',
          })
        }
      }
    }
  }

  activityItems.sort((a, b) => b.seconds - a.seconds)

  // Intelligence data
  const viewedArgs = intelligence?.argumentBreakdown.filter((a) => a.status !== 'not_viewed') ?? []
  const viewedRisks = intelligence?.riskBreakdown.filter((r) => r.status !== 'not_viewed') ?? []
  const hasIntelligence = viewedArgs.length > 0 || viewedRisks.length > 0

  // Checked against what will ACTUALLY render below, not just whether a
  // session row exists — a header-only visit has a session but produces zero
  // activityItems (profile/header events are deliberately excluded from
  // sectionTotals above) and no documentViewing, so with `showSessions`
  // off (the Share page's Visitors log) the raw session timeline — the one
  // thing that would have shown something — is suppressed too, and the
  // panel would otherwise render as a blank box instead of this message.
  const hasRenderableContent =
    activityItems.length > 0 ||
    (investor.documentViewing && investor.documentViewing.length > 0) ||
    hasIntelligence ||
    (showSessions && investor.sessions.length > 0)

  if (!hasRenderableContent && !loading) {
    return (
      <div className="border-t border-border/50 bg-[#0e0e0e] px-6 py-4">
        <p className="text-xs text-[#555]">No detailed activity recorded yet</p>
      </div>
    )
  }

  return (
    <div className="border-t border-border/50 bg-[#0e0e0e] px-6 py-4">
      {loading && (
        <div className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="size-3.5 animate-spin" />
          Loading activity data...
        </div>
      )}

      {/* Top-level summary: what they spent time on, sorted by duration */}
      {activityItems.length > 0 && (
        <div className="mb-3">
          <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Time Spent
          </p>
          <div className="space-y-1">
            {activityItems.slice(0, 5).map((item) => {
              const maxSec = Math.max(activityItems[0]?.seconds ?? 1, 1)
              const pct = (item.seconds / maxSec) * 100
              return (
                <div key={item.key} className="flex items-center gap-2">
                  <span className="w-28 shrink-0 truncate text-xs text-foreground capitalize">
                    {item.label}
                  </span>
                  <div className="h-1.5 flex-1 rounded-full bg-muted/30">
                    <div
                      className="h-1.5 rounded-full bg-blue-400/60"
                      style={{ width: `${Math.max(pct, 3)}%` }}
                    />
                  </div>
                  <span className="w-10 shrink-0 text-right text-[10px] text-muted-foreground tabular-nums">
                    {formatSeconds(item.seconds)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Document page grid — all pages shown, viewed ones highlighted */}
      {investor.documentViewing && investor.documentViewing.length > 0 && (
        <div className="mb-3">
          <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Document Pages
          </p>
          <div className="space-y-2.5">
            {investor.documentViewing.map((doc) => {
              const dwellMap = new Map(doc.pageBreakdown.map((p) => [p.page, p.seconds]))
              const maxPage = Math.max(...doc.pageBreakdown.map((p) => p.page), 0)
              const maxSec = Math.max(...doc.pageBreakdown.map((p) => p.seconds), 1)
              const pages = Array.from({ length: maxPage }, (_, i) => i + 1)

              return (
                <div key={doc.documentId}>
                  <div className="mb-1 flex items-center gap-2">
                    <FileText className="size-3 text-emerald-400" />
                    <span className="text-[11px] text-foreground">{doc.documentName}</span>
                    <span className="text-[10px] text-muted-foreground">
                      {doc.pagesViewed}/{maxPage} pages · {formatSeconds(doc.totalSeconds)}
                    </span>
                  </div>
                  <TooltipProvider delayDuration={100}>
                    <div className="flex gap-px">
                      {pages.map((page) => {
                        const seconds = dwellMap.get(page) ?? 0
                        const intensity = seconds > 0 ? seconds / maxSec : 0
                        const slideUrl = doc.slideImageUrls?.[String(page)]
                        return (
                          <Tooltip key={page}>
                            <TooltipTrigger asChild>
                              <div
                                className={cn(
                                  'h-5 flex-1 first:rounded-l last:rounded-r',
                                  seconds > 0 ? '' : 'bg-muted/15'
                                )}
                                style={
                                  seconds > 0
                                    ? {
                                        backgroundColor: `oklch(0.55 0.18 145 / ${Math.max(intensity * 0.85, 0.15)})`,
                                      }
                                    : undefined
                                }
                              />
                            </TooltipTrigger>
                            <TooltipContent side="top" className="p-1.5 text-xs">
                              {slideUrl && (
                                <img
                                  src={slideUrl}
                                  alt={`Slide ${page}`}
                                  className="mb-1 w-40 rounded border border-border/50"
                                />
                              )}
                              {seconds > 0
                                ? `Page ${page}: ${formatSeconds(seconds)}`
                                : `Page ${page}: not viewed`}
                            </TooltipContent>
                          </Tooltip>
                        )
                      })}
                    </div>
                  </TooltipProvider>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Content engagement — top 5 visible, rest collapsible */}
      {hasIntelligence && <ContentEngagement args={viewedArgs} risks={viewedRisks} />}

      {/* Session timelines (collapsible) */}
      {showSessions && investor.sessions.length > 0 && (
        <DetailSection title={`Sessions (${investor.sessions.length})`} defaultOpen={false}>
          <style>{`
            @keyframes event-dot-pulse {
              0%, 100% { transform: scale(1); opacity: 0.5; }
              50% { transform: scale(2.2); opacity: 0; }
            }
          `}</style>
          <TooltipProvider delayDuration={200}>
            <div className="space-y-2">
              {investor.sessions.map((session) => (
                <SessionTimeline key={session.date} session={session} />
              ))}
            </div>
          </TooltipProvider>
        </DetailSection>
      )}
    </div>
  )
}

function formatDuration(seconds: number): string {
  if (seconds <= 0) return '0s'
  if (seconds < 60) return `${seconds}s`
  const m = Math.floor(seconds / 60)
  const s = seconds % 60
  return s > 0 ? `${m}m ${s}s` : `${m}m`
}

/** Horizontal bar showing profile/document/video time within a session, with action dots overlaid. */
export function SessionTimeline({ session }: { session: InvestorActivity['sessions'][number] }) {
  type TimelineEvent = InvestorActivity['sessions'][number]['events'][number]

  const sessionSeconds = parseDuration(session.duration)
  if (sessionSeconds === 0 || session.events.length === 0) return null

  const timedEvents = session.events.filter(
    (e: TimelineEvent) => e.duration && parseDuration(e.duration) > 0
  )
  const instantEvents = session.events.filter(
    (e: TimelineEvent) => !e.duration || parseDuration(e.duration) === 0
  )
  const totalTimedSeconds = timedEvents.reduce(
    (sum: number, e: TimelineEvent) => sum + parseDuration(e.duration),
    0
  )

  return (
    <div className="flex items-center gap-3">
      <span className="w-24 shrink-0 text-[10px] text-[#555]">{session.date}</span>
      <div className="relative h-5 flex-1 overflow-visible rounded">
        {/* Timed bars */}
        <div className="flex h-full items-center gap-px overflow-hidden rounded">
          {timedEvents.map((event: TimelineEvent, i: number) => {
            const seconds = parseDuration(event.duration)
            const pct = totalTimedSeconds > 0 ? (seconds / totalTimedSeconds) * 100 : 0
            const tooltipLabel = `${TIMELINE_LABELS[event.type] ?? event.type}${event.label && event.type !== 'profile' ? `: ${event.label}` : ''} · ${event.duration}`

            return (
              <Tooltip key={`bar-${event.type}-${i}`}>
                <TooltipTrigger asChild>
                  <div
                    className={cn(
                      'h-full min-w-[4px] cursor-default transition-opacity hover:opacity-80',
                      TIMELINE_COLORS[event.type] ?? 'bg-[#333]'
                    )}
                    style={{ width: `${Math.max(pct, 3)}%` }}
                  />
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  {tooltipLabel}
                </TooltipContent>
              </Tooltip>
            )
          })}
        </div>

        {/* Instant event dots — absolutely positioned over the bar */}
        {instantEvents.map((event: TimelineEvent, i: number) => {
          const offsetSeconds = event.offsetSeconds ?? 0
          const pct =
            sessionSeconds > 0 ? Math.min((offsetSeconds / sessionSeconds) * 100, 100) : 50
          const tooltipLabel = `${TIMELINE_LABELS[event.type] ?? event.type}${event.label && event.label !== event.type ? `: ${event.label}` : ''}`
          const dotColor = EVENT_DOT_COLORS[event.type] ?? 'bg-[#888]'

          return (
            <Tooltip key={`dot-${event.type}-${i}`}>
              <TooltipTrigger asChild>
                <div
                  className="absolute top-1/2 z-10 flex -translate-x-1/2 -translate-y-1/2 items-center justify-center"
                  style={{ left: `${pct}%`, width: 16, height: 16 }}
                >
                  {/* Pulse ring */}
                  <div
                    className={cn('absolute size-2.5 rounded-full opacity-50', dotColor)}
                    style={{
                      animation: 'event-dot-pulse 2.5s ease-in-out infinite',
                    }}
                  />
                  {/* Solid dot */}
                  <div
                    className={cn(
                      'relative size-2.5 cursor-default rounded-full ring-1 ring-white/15',
                      dotColor
                    )}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="text-xs">
                {tooltipLabel}
              </TooltipContent>
            </Tooltip>
          )
        })}
      </div>
      <span className="w-12 shrink-0 text-right text-[10px] text-muted-foreground">
        {session.duration}
      </span>
    </div>
  )
}
